import { createAnimation, mdTransitionAnimation } from '@ionic/react'
import type { Animation } from '@ionic/react'

interface TransitionOptions {
	mode?: string
	direction?: string
	duration?: number
	easing?: string
	enteringEl: HTMLElement
	leavingEl: HTMLElement | undefined
}

const DURATION = 540
const EASING = 'cubic-bezier(0.32,0.72,0,1)'
const OFF_OPACITY = 0.8
const CENTER = '0%'

const pageElement = (el: HTMLElement): HTMLElement =>
	el.classList.contains('ion-page')
		? el
		: (el.querySelector<HTMLElement>(':scope > .ion-page, :scope > ion-nav, :scope > ion-tabs') ?? el)

// What slides: a page's content and its header and footer contents, or else a page nested in it. Ionic also
// slides a page's toolbars on their own; no page here has one outside its content.
const slidingParts = (page: HTMLElement): { content: HTMLElement | null; parts: Element[] } => {
	const content = page.querySelector<HTMLElement>(':scope > .sq-content')
	const chrome = [
		...page.querySelectorAll(':scope > header.sq-header > *:not(.sq-toolbar), :scope > footer.sq-footer > *'),
	]
	const toolbars = page.querySelectorAll(':scope > header.sq-header > .sq-toolbar')
	if (!content && !chrome.length && !toolbars.length) {
		return {
			content,
			parts: [page.querySelector(':scope > .ion-page, :scope > ion-nav, :scope > ion-tabs')].filter(Boolean),
		}
	}
	return { content, parts: [content, ...chrome].filter(Boolean) }
}

const transitionEffect = (content: HTMLElement | null, cover: [number, number], shade: [number, number]) => {
	const effect = content?.querySelector<HTMLElement>(':scope > .sq-content-transition-effect')
	if (!effect) return undefined
	return createAnimation()
		.addElement(effect)
		.beforeStyles({ opacity: '1', display: 'block' })
		.afterStyles({ opacity: '', display: '' })
		.addAnimation([
			createAnimation()
				.addElement(effect.querySelector('.sq-content-transition-cover'))
				.beforeClearStyles(['opacity'])
				.fromTo('opacity', cover[0], cover[1]),
			createAnimation()
				.addElement(effect.querySelector('.sq-content-transition-shadow'))
				.beforeClearStyles(['opacity'])
				.fromTo('opacity', shade[0], shade[1]),
		])
}

/**
 * Ionic's iOS page transition, for pages whose content is the library's: the entering page slides in over
 * the leaving one, which drifts a third of the way off under a fading cover. Material mode keeps Ionic's own.
 */
export const pageTransition = (navEl: HTMLElement, opts: TransitionOptions): Animation => {
	if (opts.mode !== 'ios') return mdTransitionAnimation(navEl, opts as never)

	const isRTL = navEl.ownerDocument.dir === 'rtl'
	const OFF_RIGHT = isRTL ? '-99.5%' : '99.5%'
	const OFF_LEFT = isRTL ? '33%' : '-33%'
	const { enteringEl, leavingEl } = opts
	const back = opts.direction === 'back'

	const root = createAnimation()
		.addElement(enteringEl)
		.duration(opts.duration || DURATION)
		.easing(opts.easing || EASING)
		.fill('both')
		.beforeRemoveClass('ion-page-invisible')
	if (leavingEl && navEl) root.addAnimation(createAnimation().addElement(navEl))

	const entering = slidingParts(enteringEl)
	const enteringContent = createAnimation().addElement(entering.parts)
	root.addAnimation(enteringContent)
	if (back) {
		enteringContent
			.beforeClearStyles(['opacity'])
			.fromTo('transform', `translateX(${OFF_LEFT})`, `translateX(${CENTER})`)
			.fromTo('opacity', OFF_OPACITY, 1)
	} else {
		enteringContent
			.beforeClearStyles(['opacity'])
			.fromTo('transform', `translateX(${OFF_RIGHT})`, `translateX(${CENTER})`)
	}
	const enteringEffect = transitionEffect(entering.content, [0, 0.1], [0.03, 0.7])
	if (enteringEffect) enteringContent.addAnimation([enteringEffect])

	if (leavingEl) {
		const leaving = slidingParts(leavingEl)
		const leavingContent = createAnimation().addElement(leaving.parts)
		root.addAnimation(leavingContent)
		if (back) {
			leavingContent
				.beforeClearStyles(['opacity'])
				.fromTo('transform', `translateX(${CENTER})`, isRTL ? 'translateX(-100%)' : 'translateX(100%)')
			const leavingPage = pageElement(leavingEl)
			root.afterAddWrite(() => {
				if (root.getDirection() === 'normal') leavingPage.style.setProperty('display', 'none')
			})
		} else {
			leavingContent
				.fromTo('transform', `translateX(${CENTER})`, `translateX(${OFF_LEFT})`)
				.fromTo('opacity', 1, OFF_OPACITY)
		}
		const leavingEffect = transitionEffect(leaving.content, [0.1, 0], [0.7, 0.03])
		if (leavingEffect) leavingContent.addAnimation([leavingEffect])
	}

	return root
}
